import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Platform, StatusBar, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';

import {
    selectAppState,
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    setMuted,
    setSpotlightVideoVisible,
    setVolume,
} from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import {
    selectPlayerIsMuted,
    selectPlayerVolume,
    selectSpotlightVideoVisible,
} from 'store/player/selectors';
import { setVideoSessionId } from 'store/session/actions';

import { UavEvents } from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import useGetPlaybackPauseUavParams from 'services/uav/useGetPlaybackPauseUavParams';
import useGetPlaybackResumeUavParams from 'services/uav/useGetPlaybackResumeUavParams';
import useGetPlaybackSkipAheadUavParams from 'services/uav/useGetPlaybackSkipAheadUavParams';
import useGetPlaybackSkipBackUavParams from 'services/uav/useGetPlaybackSkipBackUavParams';
import useGetPlaybackStartUavParams from 'services/uav/useGetPlaybackStartUavParams';
import useGetPlaybackStopUavParams from 'services/uav/useGetPlaybackStopUavParams';
import { logUavAnalytics } from 'services/uav/utils';

import { COLOR } from 'utils/colors';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import isNumber from 'utils/number.ts';
import { isEmpty } from 'utils/object';
import { isSafari, isWeb } from 'utils/responsiveness';
import { getUniqueId } from 'utils/uniqueId';

import useFrameFitting from 'screens/PlayerModal/hooks/useFrameFitting';
import usePlayerBackHandler from 'screens/PlayerModal/hooks/usePlayerBackHandler';
import useSkippings, {
    SKIP_STEP,
} from 'screens/PlayerModal/hooks/useSkippings';
import { PlayerControlsAccessLevel } from 'screens/PlayerModal/hooks/useTrickModeControl';
import { PlayerError } from 'screens/PlayerModal/types';
import {
    enterWebFullscreen,
    exitWebFullscreen,
    onAfterHidePlayerModal,
    onBeforeShowPlayerModal,
} from 'screens/PlayerModal/utils';
import { getErrorDescription } from 'screens/PlayerModal/utils/errors.ts';

import Player from 'components/Player';
import {
    BitmovinPlayerError,
    BitmovinPlayerSource,
    PlayerProps,
    PlayerRef,
} from 'components/Player/types.ts';
import PlayerOverlay from 'components/PlayerOverlay';

import styles from './styles';

const SPOTLIGHT_PLAYER_MODAL_ID = 'spotlight-player-modal';
const DASH_SOURCE: BitmovinPlayerSource = {
    src: 'https://vod.onetv-prod.one.hu/OneTV_bemutato_Android_mobile/vxfmt=dp/manifest.mpd?device_profile=DASH_STB_CLR_VOD_HD',
    type: 'dash',
};
const HLS_SOURCE: BitmovinPlayerSource = {
    src: 'https://vod.onetv-prod.one.hu/OneTV_bemutato_ios/vxfmt=dp/playlist.m3u8?device_profile=CMAFHLS_AVC_HD_CLR',
    type: 'hls',
};
const playerTracks = { textTracks: [], audioTracks: [] };
const source = Platform.select<BitmovinPlayerSource>({
    ios: HLS_SOURCE,
    android: DASH_SOURCE,
    web: isSafari ? HLS_SOURCE : DASH_SOURCE,
});

const SKIP_DEBOUNCE_TIME = 400;

// Mock video item for UAV tracking
const SPOTLIGHT_VIDEO_ITEM = {
    id: 'spotlight-video',
};

function SpotlightVideo() {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { primary = COLOR.primary } = useSelector(selectLayoutConfig);
    const appState = useSelector(selectAppState);
    const visible = useSelector(selectSpotlightVideoVisible);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);
    const muted = useSelector(selectPlayerIsMuted);
    const volume = useSelector(selectPlayerVolume);

    const videoSessionIdRef = useRef<string>('');
    const playerRef = useRef<PlayerRef | null>(null);
    const slidingInProgress = useRef(false);
    const prevIsVisible = useRef(visible);

    const [duration, setDuration] = useState(0);
    const [progress, setPorgress] = useState(0);
    const [readyToPlay, setReadyToPlay] = useState(false);
    const [paused, setPaused] = useState(false);
    const [pausedByUser, setPausedByUser] = useState(false);
    const [fullscreen, setFullscreen] = useState(!isWeb);
    const [error, setError] = useState<PlayerError>({});
    const [playerWaiting, setPlayerWaiting] = useState(false);
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);

    const { seeking, handleSkip, handleSeeked, handleResetSeeking } =
        useSkippings({
            playerRef,
            videoDuration: duration,
            playerPausedByUser: pausedByUser,
            isStartover: false,
            isCatchup: false,
        });

    const getBaseUavParams = useGetBaseUavParams();
    const { getPlaybackStartUavParams } = useGetPlaybackStartUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });
    const { getPlaybackStopUavParams } = useGetPlaybackStopUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });
    const { getPlaybackPauseUavParams } = useGetPlaybackPauseUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });
    const { getPlaybackResumeUavParams } = useGetPlaybackResumeUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });
    const { getPlaybackSkipAheadUavParams } = useGetPlaybackSkipAheadUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });
    const { getPlaybackSkipBackUavParams } = useGetPlaybackSkipBackUavParams({
        playbackType: StreamTypes.vod,
        videoItem: SPOTLIGHT_VIDEO_ITEM as any,
    });

    const frameFitting = useFrameFitting({
        visible,
        fullscreen,
        errorExists: !isEmpty(error),
        embedded: false,
    });

    const handleClose = useCallback(() => {
        dispatch(setSpotlightVideoVisible(false));
    }, []);

    const handleReady = useCallback(
        async (e: { duration: number | null | undefined }) => {
            setDuration(Math.floor(e.duration || 0));
            setReadyToPlay(true);
            playerRef.current?.play();
            setHasStartedPlayback(true);

            const startParams = getPlaybackStartUavParams({
                templateId: 'spotlight',
                railId: 'spotlight',
            });
            if (startParams) {
                logUavAnalytics(startParams);
            }
        },
        [getPlaybackStartUavParams],
    );

    const handleProgress = useCallback<PlayerProps['onProgress']>(
        (e) => {
            if (
                !slidingInProgress.current &&
                !seeking &&
                isNumber(e.relativePosition)
            ) {
                setPorgress(Math.max(0, Math.floor(e.relativePosition)));
            }
        },
        [seeking],
    );

    const handlePlayPause = useCallback(() => {
        if (paused) {
            playerRef.current?.play!();
            setPaused(false);
            setPausedByUser(false);

            const resumeParams = getPlaybackResumeUavParams({
                templateId: 'spotlight',
                railId: 'spotlight',
            });

            if (resumeParams) {
                logUavAnalytics(resumeParams);
            }
        } else {
            playerRef.current?.pause!();
            setPaused(true);
            setPausedByUser(true);

            const pauseParams = getPlaybackPauseUavParams({
                position: progress,
            });

            if (pauseParams) {
                logUavAnalytics(pauseParams);
            }
        }
    }, [
        paused,
        progress,
        getPlaybackPauseUavParams,
        getPlaybackResumeUavParams,
    ]);

    const handleSlidingStart = useCallback(() => {
        slidingInProgress.current = true;
    }, []);

    const handleSlidingComplete = useCallback(
        (progressValue: number) => {
            slidingInProgress.current = false;

            if (progressValue >= duration) {
                handleClose();
            } else {
                handleSkip({ seekPosition: progressValue });
            }
        },
        [duration, handleClose, handleSkip],
    );

    const handleSlidingCompleteSilently = useCallback(() => {
        slidingInProgress.current = false;
    }, []);

    const skipInProgress = useRef(false);
    const skipTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleSkipDebounced = useCallback(
        (target: number) => {
            playerRef.current?.pause();
            skipInProgress.current = true;

            setPorgress((prevProgress) => {
                const result = prevProgress + target;

                if (result <= 0) {
                    return Math.max(0, result);
                }

                return Math.min(result, duration);
            });
        },
        [duration],
    );

    useEffect(() => {
        if (skipInProgress.current) {
            if (skipTimeout.current != null) {
                clearTimeout(skipTimeout.current);
                skipTimeout.current = null;
            }

            skipTimeout.current = setTimeout(() => {
                handleSkip({ seekPosition: progress });
            }, SKIP_DEBOUNCE_TIME);
        }
    }, [handleSkip, progress]);

    useEffect(() => {
        videoSessionIdRef.current = getUniqueId();
        dispatch(setVideoSessionId(videoSessionIdRef.current));

        return () => {
            if (videoSessionIdRef.current) {
                dispatch(setVideoSessionId(undefined));
            }
        };
    }, [dispatch]);

    useUpdateEffect(() => {
        if (!seeking && skipInProgress.current && !pausedByUser) {
            playerRef.current?.play();
            skipInProgress.current = false;

            if (skipTimeout.current != null) {
                clearTimeout(skipTimeout.current);
                skipTimeout.current = null;
            }
        }
    }, [seeking]);

    const handleSkipBackward = useCallback(() => {
        handleSkipDebounced(-SKIP_STEP);

        const skipBackParams = getPlaybackSkipBackUavParams({
            startPosition: progress,
            endPosition: Math.max(0, progress - SKIP_STEP),
        });

        if (skipBackParams) {
            logUavAnalytics(skipBackParams);
        }
    }, [handleSkipDebounced, progress, getPlaybackSkipBackUavParams]);

    const handleSkipForward = useCallback(() => {
        handleSkipDebounced(SKIP_STEP);

        const skipAheadParams = getPlaybackSkipAheadUavParams({
            startPosition: progress,
            endPosition: Math.min(duration, progress + SKIP_STEP),
        });

        if (skipAheadParams) {
            logUavAnalytics(skipAheadParams);
        }
    }, [
        handleSkipDebounced,
        progress,
        duration,
        getPlaybackSkipAheadUavParams,
    ]);

    const handleStartover = useCallback(() => {
        if (progress > 0) {
            if (paused) {
                setPaused(false);
                setPausedByUser(false);
                handlePlayPause();
            }
            handleSkip({ seekPosition: 0 });
        } else if (paused) {
            handlePlayPause();
        }
    }, [handleSkip, paused, progress, handlePlayPause]);

    const handleWaiting = useCallback(() => {
        if (!paused) {
            setPlayerWaiting(true);
        }
    }, [paused]);

    const handlePlaying = useCallback(() => {
        setPlayerWaiting(false);
    }, []);

    const handleModalHide = useCallback(() => {
        if (hasStartedPlayback) {
            const stopParams = getPlaybackStopUavParams({
                position: progress,
                editorialID: SPOTLIGHT_VIDEO_ITEM.id,
                ...getBaseUavParams(UavEvents.PLAYBACK_STOP)?.metadata,
            });

            if (stopParams) {
                logUavAnalytics(stopParams);
            }
        }

        setDuration(0);
        setPorgress(0);
        setReadyToPlay(false);
        setPaused(false);
        setPausedByUser(false);
        setFullscreen(false);
        setError({});
        setHasStartedPlayback(false);
        handleResetSeeking();
    }, [
        handleResetSeeking,
        hasStartedPlayback,
        progress,
        getPlaybackStopUavParams,
        getBaseUavParams,
    ]);

    const handleEnterWebFullscreen = useCallback(async () => {
        if (isWeb) {
            await enterWebFullscreen(SPOTLIGHT_PLAYER_MODAL_ID);
        }
    }, []);

    const handleExitWebFullscreen = useCallback(async () => {
        if (isWeb) {
            await exitWebFullscreen();
        }
    }, []);

    const handleFullscreenChange = useCallback(() => {
        setFullscreen(!!document.fullscreenElement);
    }, []);

    const handlePlayerError = useCallback(
        (playerError: BitmovinPlayerError) => {
            setError({
                code: playerError.code,
                description: t(getErrorDescription(playerError.code)),
            });
        },
        [t],
    );

    const handleToggleMute = useCallback(() => {
        dispatch(setMuted());
    }, []);

    const handleChangeVolume = useCallback((_volume: number) => {
        dispatch(setVolume(_volume));
    }, []);

    useEffect(() => {
        if (isWeb) {
            if (visible) {
                document.addEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            } else {
                document.removeEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            }
        }
    }, [visible]);

    useEffect(() => {
        if (!isWeb) {
            if (visible) {
                setFullscreen(!isWeb);
                onBeforeShowPlayerModal();
            } else {
                onAfterHidePlayerModal();
            }
        }
    }, [visible]);

    useEffect(() => {
        if (isWeb && visible) {
            window.history.pushState({}, '', null);
            window.addEventListener('popstate', handleClose);
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('popstate', handleClose);
            }
        };
    }, [visible, handleClose]);

    usePlayerBackHandler({ visible, onHardwareBackPress: handleClose });

    useUpdateEffect(() => {
        if (!isWeb) {
            if (appState === 'active' && paused && !pausedByUser) {
                handlePlayPause();
            } else if (appState !== 'active' && !paused) {
                playerRef.current?.pause!();
                setPaused(true);
            }
        }
    }, [appState]);

    useEffect(() => {
        if (prevIsVisible.current && !visible) {
            handleModalHide();
        }

        prevIsVisible.current = visible;
    }, [visible]);

    if (!visible) {
        return null;
    }

    return (
        <View
            id={SPOTLIGHT_PLAYER_MODAL_ID}
            style={styles.root}
            accessible={false}
            accessibilityViewIsModal
        >
            <StatusBar translucent={visible} hidden={visible} />
            <GestureHandlerRootView style={styles.container} accessible={false}>
                <View style={styles.safeArea} accessible={false}>
                    <Player
                        ref={playerRef}
                        style={
                            frameFitting.frameFittingOn && readyToPlay
                                ? styles.fitPlayer
                                : styles.player
                        }
                        source={source}
                        muted={muted}
                        volume={volume}
                        onReady={handleReady}
                        onProgress={handleProgress}
                        onPlaying={handlePlaying}
                        onWaiting={handleWaiting}
                        onSeeked={handleSeeked}
                        onEnd={handleClose}
                        onError={handlePlayerError}
                    />
                    <PlayerOverlay
                        startoverAllowed={isEmpty(error)}
                        startoverButtonVisible={isEmpty(error)}
                        isCatchup
                        playerError={error}
                        muted={muted}
                        volume={volume}
                        seeking={seeking}
                        timeshiftModeOn={false}
                        timeshiftPosition={null}
                        timeshiftDuration={undefined}
                        jumpToLiveAllowed={false}
                        noTitleBlock
                        skipBackwardDisabled={false}
                        skipForwardDisabled={false}
                        pauseAccessLevel={PlayerControlsAccessLevel.ENABLED}
                        rewindAccessLevel={PlayerControlsAccessLevel.ENABLED}
                        fastforwardAccessLevel={
                            PlayerControlsAccessLevel.ENABLED
                        }
                        isLive={false}
                        isVod={false}
                        isStartover={false}
                        isNpvr={false}
                        readyToPlay={readyToPlay}
                        playerWaiting={playerWaiting}
                        isPaused={paused}
                        fullscreen={fullscreen}
                        position={progress}
                        duration={duration}
                        playerTracks={playerTracks}
                        primaryColor={primary}
                        screenReaderEnabled={screenReaderEnabled}
                        frameFitting={frameFitting}
                        onPlayPausePress={handlePlayPause}
                        onSlidingStart={handleSlidingStart}
                        onSlidingComplete={handleSlidingComplete}
                        onSlidingCompleteSilently={
                            handleSlidingCompleteSilently
                        }
                        onSlidingValueChanged={setPorgress}
                        onSkipBackward={handleSkipBackward}
                        onSkipForward={handleSkipForward}
                        onCloseModal={handleClose}
                        onFullscreenPress={handleEnterWebFullscreen}
                        onMinimizePress={handleExitWebFullscreen}
                        onStartover={handleStartover}
                        onToggleMute={handleToggleMute}
                        onChangeVolume={handleChangeVolume}
                    />
                </View>
            </GestureHandlerRootView>
        </View>
    );
}

export default SpotlightVideo;
